import { LegalPage, Section, CONTACT } from "@/components/LegalPage";

const S = (props) => <Section prefix="privacy" {...props} />;

export default function Privacy() {
  return (
    <LegalPage title="Privacy policy" testId="privacy-page">
      <>
        <p className="mt-6 text-[15px] leading-relaxed text-ink-soft">
          Pitchpaper (ABN 87 571 984 745) runs pitchpaper.au. We respect your privacy and handle personal information in line
          with the Australian Privacy Principles in the <em>Privacy Act 1988</em> (Cth). This page explains what we collect, why, and the choices you have.
        </p>

        <S title="What we collect" id="collect">
          <ul className="list-disc space-y-2 pl-5">
            <li><strong className="text-ink">Account details:</strong> your email address and password, handled by our sign-in provider. We never see or store your password.</li>
            <li><strong className="text-ink">Your documents:</strong> everything you type into a resume or cover letter, such as your name, contact details, work history, education and skills.</li>
            <li><strong className="text-ink">Purchases:</strong> what you bought, the amount, the GST and your billing country. Card details go straight to Stripe and never reach us.</li>
            <li><strong className="text-ink">Usage data:</strong> pages visited, device and browser type, and approximate location, collected by Google Analytics using cookies.</li>
          </ul>
        </S>

        <S title="How we use it" id="use">
          <ul className="list-disc space-y-2 pl-5">
            <li>To run your account and save your documents so you can come back to them.</li>
            <li>To create AI suggestions, cover letters, match scores and imports that you ask for.</li>
            <li>To take payments, unlock downloads and send your tax invoice by email.</li>
            <li>To understand how people use Pitchpaper so we can improve it.</li>
          </ul>
          <p>We don't sell your personal information and we don't use your resumes for advertising.</p>
        </S>

        <S title="AI writing help" id="ai">
          <p>When you use an AI feature, the relevant resume or job posting text is sent to our AI provider to produce the result. It is used only to answer that request.</p>
          <p>When you import a PDF or Word file, we read the text once to fill in the form and then discard the file. We don't keep a copy.</p>
        </S>

        <S title="Payments" id="payments">
          <p>Payments are processed by Stripe. Stripe collects your card and billing details under its own privacy policy. We receive only a confirmation, the amount, the GST and your email so we can issue your invoice.</p>
        </S>

        <S title="Analytics and cookies" id="analytics">
          <p>We use Google Analytics to count visits and see which pages help people most. It sets cookies in your browser. You can block or delete cookies in your browser settings, or install Google's opt-out add-on, and Pitchpaper will still work.</p>
        </S>

        <S title="Shared resume links" id="sharing">
          <p>If you turn on a public link for a resume, anyone with that link can view it, including the contact details on it. You can turn the link off at any time and it stops working straight away.</p>
        </S>

        <S title="Who we share it with" id="providers">
          <p>We use trusted service providers to run Pitchpaper: hosting and database, sign-in, Stripe (payments), an AI model provider (writing help), an email provider (invoices) and Google (analytics).
            Some of these store or process data outside Australia, including in the United States. We only share what each one needs to do its job.</p>
          <p>We may also disclose information if the law requires it.</p>
        </S>

        <S title="How long we keep it" id="retention">
          <p>We keep your account and documents until you delete them or ask us to close your account. We keep invoices and payment records for as long as Australian tax law requires, which is generally five years.</p>
        </S>

        <S title="Your choices" id="rights">
          <p>You can view, edit and delete your documents at any time in My documents. To get a copy of your information, correct it, close your account or make a complaint, email us at{" "}
            <a href={`mailto:${CONTACT}`} className="text-thread hover:underline" data-testid="privacy-contact-email">{CONTACT}</a>. We aim to reply within 30 days.</p>
          <p>If you're not happy with our response, you can contact the Office of the Australian Information Commissioner at{" "}
            <a href="https://www.oaic.gov.au" target="_blank" rel="noreferrer" className="text-thread hover:underline">oaic.gov.au</a>.</p>
        </S>

        <S title="Changes" id="changes">
          <p>If we change this policy, we'll update the date at the top of this page.</p>
        </S>
      </>
    </LegalPage>
  );
}
